import type { Finding, RunDocumentParagraph, Severity } from "../../services/lensService";
import { isNotAssessed } from "./lensUtils";

// Where a finding sits in the original. A not-assessed section runs from its paragraph up to the next heading.
export type Placement =
    | { kind: "text"; index: number; start: number; end: number }
    | { kind: "paragraph"; index: number }
    | { kind: "heading"; index: number }
    | { kind: "section"; index: number; end: number }
    | { kind: "unlocated" };

export interface TextMark {
    findingKey: string;
    start: number;
    end: number;
}

// What one paragraph shows: the findings badged on it, the text to highlight, and whether it is inside a
// not-assessed section.
export interface ParagraphMarks {
    badges: Finding[];
    textMarks: TextMark[];
    wholeParagraph: boolean;
    notAssessed: boolean;
}

export interface Layout {
    placements: Record<string, Placement>;
    marks: Map<number, ParagraphMarks>;
    omissions: Finding[];
    // Everything but omissions, in the order it appears in the document; unlocated last.
    ordered: Finding[];
}

export const SEVERITY_RANK: Record<Severity, number> = { critical: 0, material: 1, preference: 2 };

export const SEVERITY_LABEL: Record<Severity, string> = { critical: "Critical", material: "Material", preference: "Preference" };

const NUMBERED_HEADING = /^\d{1,2}\.?\s+\S/;
const TRAILING_PUNCTUATION = /[.;:,!?]$/;
const HEADING_FIRST_CHARACTER = /^[A-Z0-9"'(£]/;
const CONNECTIVE = /^(and|or|and\/or)\b/i;

// The review's own rule for unstyled headings: a short numbered line, or a short unpunctuated one.
export const isHeadingLike = (paragraph: RunDocumentParagraph) => {
    if (paragraph.isHeading || paragraph.style === "Title") return true;
    const text = paragraph.text.trim();
    if (text.length >= 60 || CONNECTIVE.test(text)) return false;
    if (NUMBERED_HEADING.test(text)) return true;
    return text.split(/\s+/).length <= 7 && !TRAILING_PUNCTUATION.test(text) && HEADING_FIRST_CHARACTER.test(text);
};

const normalise = (value: string) =>
    value
        .toLowerCase()
        .replace(/^\(?\d{1,3}(\.\d{1,3})*[.):]?\s+/, "")
        .replace(/\s+/g, " ")
        .trim();

// The heading paragraph nearest before the finding's own paragraph, else the first that matches.
const findHeading = (paragraphs: RunDocumentParagraph[], heading: string | null | undefined, before: number | null) => {
    const wanted = normalise(heading ?? "");
    if (!wanted) return null;
    const matches = paragraphs.filter((p) => {
        const text = normalise(p.text);
        return text === wanted || (text.startsWith(wanted) && isHeadingLike(p));
    });
    if (matches.length === 0) return null;
    const earlier = before == null ? [] : matches.filter((p) => p.index <= before);
    return (earlier.length > 0 ? earlier[earlier.length - 1] : matches[0]).index;
};

const place = (paragraphs: RunDocumentParagraph[], finding: Finding): Placement => {
    const index =
        finding.paragraphIndex != null && finding.paragraphIndex >= 0 && finding.paragraphIndex < paragraphs.length
            ? finding.paragraphIndex
            : null;

    if (isNotAssessed(finding)) {
        const start = index ?? findHeading(paragraphs, finding.heading, null);
        if (start == null) return { kind: "unlocated" };
        let end = start + 1;
        while (end < paragraphs.length && !isHeadingLike(paragraphs[end])) end++;
        return { kind: "section", index: start, end };
    }

    if (index != null) {
        const text = paragraphs[index].text;
        for (const candidate of [finding.fix?.find, finding.evidence]) {
            const wanted = candidate?.trim();
            const at = wanted ? text.indexOf(wanted) : -1;
            if (wanted && at >= 0) return { kind: "text", index, start: at, end: at + wanted.length };
        }
        return { kind: "paragraph", index };
    }

    const heading = findHeading(paragraphs, finding.heading, null);
    return heading == null ? { kind: "unlocated" } : { kind: "heading", index: heading };
};

const bySeverity = (a: Finding, b: Finding) =>
    Number(isNotAssessed(a)) - Number(isNotAssessed(b)) || SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];

export const layoutFindings = (paragraphs: RunDocumentParagraph[], findings: Finding[]): Layout => {
    const placements: Record<string, Placement> = {};
    const marks = new Map<number, ParagraphMarks>();
    const at = (index: number) => {
        let entry = marks.get(index);
        if (!entry) marks.set(index, (entry = { badges: [], textMarks: [], wholeParagraph: false, notAssessed: false }));
        return entry;
    };

    const omissions = findings.filter((f) => f.type === "omission");
    const placed = findings.filter((f) => f.type !== "omission");

    for (const finding of placed) {
        const placement = place(paragraphs, finding);
        placements[finding.key] = placement;
        if (placement.kind === "unlocated") continue;

        at(placement.index).badges.push(finding);
        if (placement.kind === "text")
            at(placement.index).textMarks.push({ findingKey: finding.key, start: placement.start, end: placement.end });
        else if (placement.kind === "section")
            for (let i = placement.index; i < placement.end; i++) at(i).notAssessed = true;
        else at(placement.index).wholeParagraph = true;
    }

    for (const entry of marks.values()) entry.badges.sort(bySeverity);

    const position = (finding: Finding) => {
        const placement = placements[finding.key];
        if (placement.kind === "unlocated") return [Number.MAX_SAFE_INTEGER, 0];
        return [placement.index, placement.kind === "text" ? placement.start : -1];
    };
    const ordered = [...placed].sort((a, b) => {
        const [ai, as] = position(a);
        const [bi, bs] = position(b);
        return ai - bi || as - bs || bySeverity(a, b);
    });

    return { placements, marks, omissions, ordered };
};

export interface Segment {
    text: string;
    // The findings highlighting this stretch of text, most severe first; empty for plain text.
    findingKeys: string[];
}

// Splits a paragraph at every highlight boundary, so overlapping highlights each stay clickable.
export const segment = (text: string, textMarks: TextMark[], severityOf: (key: string) => number): Segment[] => {
    if (textMarks.length === 0) return [{ text, findingKeys: [] }];

    const cuts = Array.from(new Set([0, text.length, ...textMarks.flatMap((m) => [m.start, m.end])])).sort((a, b) => a - b);
    const segments: Segment[] = [];
    for (let i = 0; i < cuts.length - 1; i++) {
        const [from, to] = [cuts[i], cuts[i + 1]];
        if (from === to) continue;
        const findingKeys = textMarks
            .filter((m) => m.start <= from && m.end >= to)
            .map((m) => m.findingKey)
            .sort((a, b) => severityOf(a) - severityOf(b));
        segments.push({ text: text.slice(from, to), findingKeys });
    }
    return segments;
};
